import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { toast } from 'material-react-toastify';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useThemeColor } from '../../hooks/useThemeColor';
import { NavMenuItem } from '../../interfaces/Settings/NavMenuItem';
import { useClickMenuItem } from '../AppHeader/useClickMenuItem';
import { settingsAtom } from '../../atoms/Settings';
import { PopularCategory } from '../../interfaces/Category/PopularCategory';
import { getPopularCategories } from '../../api/PopularCategory';

const PopularCategoriesMenu = () => {
  const settings = useRecoilValue(settingsAtom);

  const [open, setOpen] = useState(false);
  const [popularCategories, setPopularCategories] = useState<PopularCategory[]>(
    [],
  );

  const { secondary } = useThemeColor();

  const navigate = useNavigate();

  useEffect(() => {
    getPopularCategories()
      .then((p) => setPopularCategories(p))
      .catch((err) => toast.error(err.message));
  }, []);

  return popularCategories.length ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box
        onClick={() => setOpen((x) => !x)}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: open ? 2 : 0,
        }}
      >
        <Typography
          sx={{ fontSize: '18px', fontWeight: 600, color: secondary }}
        >
          {settings.settingsV1.popular_categories_text}
        </Typography>

        <IconButton
          sx={{
            transform: open ? 'rotate(180deg)' : undefined,
            p: 0,
            color: secondary,
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      {open &&
        popularCategories.map((category, i) => (
          <Button
            onClick={() => navigate(`/kategori/${category.slug}`)}
            key={i}
            sx={{
              color: secondary,
              fontSize: '14px',
              fontWeight: 600,
              width: 'fit-content',
              p: 0,
              minWidth: 0,
              textTransform: 'none',
              textAlign: 'left',
            }}
          >
            {category.name}
          </Button>
        ))}
    </Box>
  ) : null;
};

type FooterMenuProps = {
  title: string;
  menuItems: NavMenuItem[];
};

const FooterMenu = ({ title, menuItems }: FooterMenuProps) => {
  const [open, setOpen] = useState(false);

  const onClick = useClickMenuItem();

  const { secondary } = useThemeColor();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box
        onClick={() => setOpen((x) => !x)}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: open ? 2 : 0,
        }}
      >
        <Typography
          sx={{ fontSize: '18px', fontWeight: 600, color: secondary }}
        >
          {title}
        </Typography>

        <IconButton
          sx={{
            transform: open ? 'rotate(180deg)' : undefined,
            p: 0,
            color: secondary,
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      {open &&
        menuItems.map((item, i) => (
          <Button
            onClick={() => onClick(item)}
            key={i}
            sx={{
              color: secondary,
              fontSize: '14px',
              fontWeight: 600,
              width: 'fit-content',
              p: 0,
              minWidth: 0,
              textTransform: 'none',
              textAlign: 'left',
            }}
          >
            {item.name}
          </Button>
        ))}
    </Box>
  );
};

export const FooterMobile = () => {
  const settings = useRecoilValue(settingsAtom);

  const { primary, secondary } = useThemeColor();

  const navigateTo = useCallback(
    (url: string) => window.open(url, '_blank'),
    [],
  );
  const navigate = useNavigate();

  const logo = settings.settingsV1.storefront_header_logo;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        pt: 3,
        px: 1.5,
        pb: 1.5,
        backgroundColor: primary,
        boxSizing: 'border-box',
        alignItems: 'center',
        mt: 'auto',
      }}
    >
      <PopularCategoriesMenu />

      <FooterMenu
        title={settings.settingsV1.storefront_footer_menu_one_title}
        menuItems={settings.footer1}
      />
      <FooterMenu
        title={settings.settingsV1.storefront_footer_menu_two_title}
        menuItems={settings.footer2}
      />

      <Divider color={secondary} sx={{ width: '100%' }} />

      {logo ? (
        <Box
          onClick={() => navigate('')}
          component='img'
          src={logo}
          height={80}
          width='auto'
          sx={{ cursor: 'pointer' }}
        />
      ) : (
        <Typography sx={{ color: secondary }}>
          {settings.settingsV1.storefront_navbar_text}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        {!!settings.settingsV1.storefront_facebook_link && (
          <IconButton
            onClick={() =>
              navigateTo(settings.settingsV1.storefront_facebook_link)
            }
          >
            <FacebookIcon sx={{ color: secondary }} />
          </IconButton>
        )}

        {!!settings.settingsV1.storefront_twitter_link && (
          <IconButton
            onClick={() =>
              navigateTo(settings.settingsV1.storefront_twitter_link)
            }
          >
            <XIcon sx={{ color: secondary }} />
          </IconButton>
        )}

        {!!settings.settingsV1.storefront_instagram_link && (
          <IconButton
            onClick={() =>
              navigateTo(settings.settingsV1.storefront_instagram_link)
            }
          >
            <InstagramIcon sx={{ color: secondary }} />
          </IconButton>
        )}

        {!!settings.settingsV1.storefront_youtube_link && (
          <IconButton
            onClick={() =>
              navigateTo(settings.settingsV1.storefront_youtube_link)
            }
          >
            <YouTubeIcon sx={{ color: secondary }} />
          </IconButton>
        )}

        {!!settings.settingsV1.storefront_linkedin_link && (
          <IconButton
            onClick={() =>
              navigateTo(settings.settingsV1.storefront_linkedin_link)
            }
          >
            <LinkedInIcon sx={{ color: secondary }} />
          </IconButton>
        )}
      </Box>

      <Typography
        sx={{
          color: secondary,
          fontSize: '14px',
          fontWeight: 600,
        }}
      >
        © 2025 NNET E-Ticaret. Tüm hakları saklıdır.
      </Typography>
    </Box>
  );
};
